import type { ConnectionState } from '../multiWealthica2Store';
import { storeLogger, EVENTS } from './logging';

// Local state typing to avoid importing the full store (prevents runtime circular deps)
type LocalState = {
  connections: Map<string, ConnectionState>;
  restorationAttempted: boolean;
  restorationInProgress: boolean;
  restorationSource: string | null;
};

type SetFn = (partial: Partial<LocalState> | ((state: LocalState) => Partial<LocalState>)) => void;
type GetFn = () => LocalState;

export const createRestorationActions = (set: unknown, get: unknown) => ({
  // === RESTORATION COORDINATION ===
  attemptRestoration: async (source: string) => {
    const debugId = `restore-${Date.now()}-${source}`;
    const currentState = (get as GetFn)();

    storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
      phase: 'attempt',
      debugId,
      source,
      currentAttempted: currentState.restorationAttempted,
      currentInProgress: currentState.restorationInProgress,
      connectionCount: currentState.connections.size
    });

    // Skip if restoration already attempted or in progress
    if (currentState.restorationAttempted || currentState.restorationInProgress) {
      storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
        phase: 'skip',
        debugId,
        source,
        restorationAttempted: currentState.restorationAttempted,
        restorationInProgress: currentState.restorationInProgress
      });
      return Promise.resolve(false);
    }

    // Mark restoration in progress
    (set as SetFn)({
      restorationInProgress: true,
      restorationSource: source
    });

    try {
      const connections = Array.from(currentState.connections.values());
      storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
        phase: 'validate',
        debugId,
        connectionCount: connections.length,
        connectionIds: connections.map(c => c.institutionId)
      });

      if (connections.length === 0) {
        storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, { phase: 'none', debugId });
        (set as SetFn)({
          restorationInProgress: false,
          restorationAttempted: true
        });
        return Promise.resolve(true);
      }

      // Import storage utilities
      const { WealthicaStorage } = await import('../../providers/wealthica2/utils/storage');

      // CORRECTED AUTH MODEL: Single user token covers ALL institutions
      // Wealthica API uses one token per user, not per institution
      const hasUserToken = WealthicaStorage.hasToken();
      const updatedConnections = new Map(currentState.connections);
      let restoredCount = 0;

      if (hasUserToken) {
        // Restore ALL connections since user has valid token
        for (const connection of connections) {
          updatedConnections.set(connection.institutionId, {
            ...connection,
            status: 'connected',
            isConnected: true,
            error: null
          });
          restoredCount++;

          storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
            phase: 'restored',
            debugId,
            institutionId: connection.institutionId,
            providerName: connection.providerName
          });
        }
      } else {
        // Mark ALL connections as needing re-authentication
        for (const connection of connections) {
          updatedConnections.set(connection.institutionId, {
            ...connection,
            status: 'disconnected',
            isConnected: false,
            error: 'User token expired or missing'
          });

          storeLogger.warn('multiWealthica2Store', EVENTS.RESTORATION, {
            phase: 'need_reauth',
            debugId,
            institutionId: connection.institutionId,
            reason: 'No valid user token found'
          });
        }
      }

      // Update store with restored connections
      (set as SetFn)({
        connections: updatedConnections,
        restorationInProgress: false,
        restorationAttempted: true
      });

      storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
        phase: 'done',
        debugId,
        source,
        totalConnections: connections.length,
        restoredCount,
        failedCount: connections.length - restoredCount
      });

      return Promise.resolve(true);

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown restoration error';
      storeLogger.error('multiWealthica2Store', EVENTS.RESTORATION, {
        phase: 'failed',
        debugId,
        source,
        error: errorMessage
      });

      (set as SetFn)({
        restorationInProgress: false,
        restorationAttempted: true // Mark attempted even on failure to prevent loops
      });

      return Promise.resolve(false);
    }
  },

  markRestorationAttempted: (source: string) => {
    const debugId = `mark-restore-${Date.now()}-${source}`;

    storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
      phase: 'mark_attempted',
      debugId,
      source,
      timestamp: new Date().toISOString()
    });

    (set as SetFn)({
      restorationAttempted: true,
      restorationSource: source
    });
  },

  completeRestoration: () => {
    const debugId = `complete-restore-${Date.now()}`;

    storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
      phase: 'complete',
      debugId,
      timestamp: new Date().toISOString()
    });

    (set as SetFn)({
      restorationInProgress: false,
      restorationAttempted: true
    });
  },

  resetRestorationState: () => {
    const debugId = `reset-restore-${Date.now()}`;

    storeLogger.info('multiWealthica2Store', EVENTS.RESTORATION, {
      phase: 'reset',
      debugId,
      timestamp: new Date().toISOString()
    });

    (set as SetFn)({
      restorationAttempted: false,
      restorationInProgress: false,
      restorationSource: null
    });
  }
});
